import { MutableRefObject, RefObject, useLayoutEffect } from "react";
import MarkerCluster from "@googlemaps/markerclustererplus";
import { Calculator } from "@googlemaps/markerclustererplus/dist/markerclusterer";
import { addModalTextToMarker, defaultClusterOptions } from "../utils";

/**
 * Markers that are static on the map and should only update on load
 * If a marker will need to be updated after the fact it should be a MovingMapMarker
 */
export interface MapMarker {
    latLng: google.maps.LatLngLiteral;
    modalText?: string;
    icon?: google.maps.Icon;
    label?: google.maps.MarkerLabel;
    zIndex?: number;
  }

export type ClusterDetails = {
    icon: string;
    className?: string;
    minimumClusterSize?: number;
    calculator?: Calculator;
};

export const useStationaryMarkers = (
    googleMapRef: RefObject<google.maps.Map>,
    markersRef: MutableRefObject<google.maps.Marker[]>,
    markerClusterRef: RefObject<MarkerCluster>,
    markers: MapMarker[],
    markersClusterDetails?: ClusterDetails,
) => {
    // Everytime the markers are updated, we loop through delete the old markers, and add new ones
    useLayoutEffect(() => {
        if (googleMapRef.current) {
            markersRef.current.forEach((m) => m.setMap(null));
            markersRef.current = markers.map(
                ({
                    latLng, modalText, icon, label, zIndex,
                }) => {
                    const marker = new google.maps.Marker({
                        position: latLng,
                        map: googleMapRef.current,
                        icon,
                        label,
                        zIndex,
                    });

                    // If there is some text associated with the marker we create an info window
                    // The state is stored in a closure below (which works due to js magic)
                    // and keeps track of the click so the user can
                    // toggle open and close on the marker
                    if (modalText && googleMapRef.current) {
                        addModalTextToMarker(modalText, googleMapRef.current, marker);
                    }

                    return marker;
                },
            );
        }
    }, [markers]);

    useLayoutEffect(() => {
        if (markersClusterDetails && googleMapRef.current) {
            const {
                icon, className, minimumClusterSize = 2, calculator,
            } = markersClusterDetails;

            markerClusterRef.current?.getMarkers?.().forEach((m) => m.setMap(null));

            // Cluster the map icons if they are close enough together into a special blue
            // icon with the marker labels next to it (A/B/C) to show grouped markers
            (markerClusterRef.current as unknown) = new MarkerCluster(
                googleMapRef.current,
                markersRef.current,
                {
                    ...defaultClusterOptions(icon, minimumClusterSize, className),
                    calculator,
                },
            );
        }
    }, [markersClusterDetails, markers]);
};
